import classNames from "classnames";
import {useState, useEffect, useMemo} from "react";
import {useNavigate, useLocation, useSearchParams} from "react-router-dom";
import {common as stringsCommon, verify as strings} from "strings";

import Button from "components/Button";
import RadioButton from "components/RadioButton";
import TextInput from "components/TextInput";
import UnauthLayout from "components/UnauthLayout";

import * as models from "liberty-core/api/models";
import {
  useRegisterSelection,
  useRegisterVerification,
} from "liberty-core/api/react-query/user";

import {useAlert} from "lib/alert";

import styles from "./VerifyPage.module.css";

type NavigationState = {
  selections?: models.VerificationSelection[];
  temporaryPatientId?: string;
  isNewUser?: boolean;
};

export default function VerifyPage() {
  const alert = useAlert();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const locationState = location.state as NavigationState;
  const selections = useMemo(
    () => locationState?.selections ?? [],
    [locationState],
  );
  const temporaryPatientId = useMemo(
    () => locationState?.temporaryPatientId ?? "",
    [locationState],
  );
  const isNewUser = useMemo(
    () => locationState?.isNewUser ?? false,
    [locationState],
  );

  const [step, setStep] = useState(0);
  const [selectedId, setSelectedId] = useState(selections[0]?.id ?? null);
  const [code, setCode] = useState("");
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);

  const {mutateAsync: submitRegisterSelectionRequest} = useRegisterSelection();
  const {mutateAsync: submitRegisterVerificationRequest} =
    useRegisterVerification();

  useEffect(() => {
    if (!selections.length || !temporaryPatientId) {
      navigate(`/register?${searchParams.toString()}`, {replace: true});
    }
  }, [navigate, selections, temporaryPatientId, searchParams]);

  const sendCode = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    if (isSendingCode || isVerifyingCode || selectedId === null) {
      return;
    }

    setIsSendingCode(true);
    submitRegisterSelectionRequest({
      selectionId: selectedId,
      temporaryPatientId: temporaryPatientId,
    })
      .then(() => {
        setStep(1);
      })
      .catch((err) => {
        alert({
          title: stringsCommon.whoops,
          message:
            err?.response?.data?.generic?.join("\n") ??
            strings.genericSelectionErrorMessage,
        });
      })
      .finally(() => setIsSendingCode(false));
  };

  const resendCode = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    if (isSendingCode || isVerifyingCode) {
      return;
    }

    setIsSendingCode(true);
    submitRegisterSelectionRequest({
      selectionId: selectedId,
      temporaryPatientId: temporaryPatientId,
    })
      .then(() => {
        const selection = selections.find((s) => s.id === selectedId);
        alert({
          title: stringsCommon.success,
          message: strings.successSelectionMessage(selection?.value),
        });
      })
      .catch((err) => {
        alert({
          title: stringsCommon.whoops,
          message:
            err?.response?.data?.generic?.join("\n") ??
            strings.genericSelectionErrorMessage,
        });
      })
      .finally(() => setIsSendingCode(false));
  };

  const verifyCode = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    if (isSendingCode || isVerifyingCode) {
      return;
    }

    setIsVerifyingCode(true);
    submitRegisterVerificationRequest({challenge: code, temporaryPatientId})
      .then(() => {
        alert({
          title: stringsCommon.success,
          message: isNewUser
            ? strings.successVerifyMessageNewUser
            : strings.successVerifyMessage,
          callbackFn: isNewUser
            ? () => {
                navigate(`/`, {replace: true});
              }
            : () => {
                navigate(`/login?${searchParams.toString()}`, {replace: true});
              },
          confirmLabel: isNewUser ? stringsCommon.ok : strings.continue,
        });
      })
      .catch((err) => {
        alert({
          title: stringsCommon.whoops,
          message:
            err?.response?.data?.generic?.join("\n") ??
            strings.genericVerifyErrorMessage,
        });
      })
      .finally(() => setIsVerifyingCode(false));
  };

  function renderSelection(
    id: number,
    type: "Email" | "PhoneNumber",
    value: string,
    selected: boolean,
  ) {
    let prompt = "";
    if (type === "Email") {
      prompt = strings.emailPrompt;
    } else if (type === "PhoneNumber") {
      prompt = strings.textPrompt;
    }

    return (
      <button
        key={id}
        className={classNames(
          styles.selectionButton,
          selected && styles.selected,
        )}
        onClick={() => setSelectedId(id)}
        type="button"
      >
        <RadioButton checked={selected} />
        <div className={styles.radioText}>
          <div>{prompt}</div>
          <div className={styles.radioTextBold}>{value}</div>
        </div>
      </button>
    );
  }

  function renderStep0() {
    return (
      <form className={styles.verifyForm}>
        <div className={styles.verifyTitle}>Verify Account</div>
        <div
          className={classNames(styles.verifyDescription, styles.verifyText)}
        >
          {strings.step0Instructions}
        </div>
        {selections.map((s) =>
          renderSelection(
            s.id,
            s.selectionType,
            s.value ?? "",
            selectedId === s.id,
          ),
        )}
        <Button
          className={styles.verifyButton}
          variant="filled"
          onClick={sendCode}
          disabled={selectedId === null}
          loading={isSendingCode}
        >
          {strings.sendVerification}
        </Button>
      </form>
    );
  }

  function renderStep1() {
    const selection = selections.find((s) => s.id === selectedId);

    return (
      <form className={styles.verifyForm}>
        <div className={styles.verifyTitle}>Verify Account</div>
        <div
          className={classNames(styles.verifyText, styles.verifyDescription)}
        >
          {strings.step1Instructions(selection?.value)}
        </div>
        <TextInput
          className={styles.verifyInput}
          label={strings.enterCode}
          type="text"
          autoComplete="one-time-code"
          value={code}
          onChangeText={setCode}
        />
        <Button
          className={styles.resendButton}
          type="button"
          variant="minimal"
          onClick={resendCode}
          loading={isSendingCode}
          disabled={isVerifyingCode}
        >
          {strings.resendCode}
        </Button>
        <Button
          type="submit"
          disabled={!code || isSendingCode}
          onClick={verifyCode}
          loading={isVerifyingCode}
        >
          {strings.verifyAccount}
        </Button>
      </form>
    );
  }

  return (
    <UnauthLayout
      rightLink={{
        label: stringsCommon.register,
        to: `/register?${searchParams.toString()}`,
      }}
    >
      {step === 0 ? renderStep0() : null}
      {step === 1 ? renderStep1() : null}
    </UnauthLayout>
  );
}
